import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, VStack, Text, Badge, HStack, Box, Select,
} from '@chakra-ui/react';
import { FiBell, FiBellOff } from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

// Generate simple beep sounds using Web Audio API
function playBeep(type, loop) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const createBeep = (freq, duration, startTime) => {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = freq; osc.type = type === 'square_beep' ? 'square' : type === 'soft_chime' ? 'sine' : 'triangle';
      gain.gain.setValueAtTime(0.3, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      osc.start(startTime); osc.stop(startTime + duration);
    };
    if (type === 'single_beep') {
      createBeep(880, 0.3, ctx.currentTime);
      return { stop: () => ctx.close() };
    }
    // Repeated/looping sounds
    let interval;
    const play = () => {
      if (type === 'repeated_beep') { createBeep(880, 0.15, ctx.currentTime); createBeep(880, 0.15, ctx.currentTime + 0.3); }
      else if (type === 'square_beep') { createBeep(660, 0.2, ctx.currentTime); createBeep(880, 0.2, ctx.currentTime + 0.25); }
      else if (type === 'soft_chime') { createBeep(523, 0.4, ctx.currentTime); createBeep(659, 0.4, ctx.currentTime + 0.2); createBeep(784, 0.4, ctx.currentTime + 0.4); }
      else if (type === 'urgent_siren') { createBeep(600, 0.2, ctx.currentTime); createBeep(900, 0.2, ctx.currentTime + 0.2); createBeep(600, 0.2, ctx.currentTime + 0.4); }
    };
    play();
    if (loop) interval = setInterval(play, 3000);
    return { stop: () => { if (interval) clearInterval(interval); ctx.close(); } };
  } catch(e) { return { stop: () => {} }; }
}

const SOUND_OPTIONS = [
  { value: 'silent', label: 'Silent (no sound)' },
  { value: 'single_beep', label: 'Single Beep' },
  { value: 'repeated_beep', label: 'Repeated Beep (until acknowledged)' },
  { value: 'square_beep', label: 'Alert Tone (until acknowledged)' },
  { value: 'soft_chime', label: 'Soft Chime (until acknowledged)' },
  { value: 'urgent_siren', label: 'Urgent Siren (until acknowledged)' },
];

export { SOUND_OPTIONS };

export default function AlarmPopup() {
  const { user } = useAuth();
  const [activeAlarm, setActiveAlarm] = useState(null);
  const [snoozeMinutes, setSnoozeMinutes] = useState(10);
  const soundRef = useRef(null);

  const alarmMode = user?.alarm_mode || 'calendar_only';
  const alarmSound = user?.alarm_sound || 'single_beep';

  const checkAlarms = useCallback(async () => {
    if (alarmMode === 'silence') return;
    try {
      const { data } = await api.get('/calendar/alarms/active');
      if (data.length > 0 && !activeAlarm) {
        setActiveAlarm(data[0]);
        // Play sound
        if (alarmSound !== 'silent') {
          const loop = alarmSound !== 'single_beep';
          soundRef.current = playBeep(alarmSound, loop);
        }
      }
    } catch(e) {}
  }, [alarmMode, alarmSound, activeAlarm]);

  useEffect(() => {
    if (alarmMode === 'silence') return;
    checkAlarms();
    const interval = setInterval(checkAlarms, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [checkAlarms, alarmMode]);

  const stopSound = () => { if (soundRef.current) { soundRef.current.stop(); soundRef.current = null; } };

  const handleAcknowledge = async () => {
    if (!activeAlarm) return;
    stopSound();
    try { await api.post(`/calendar/${activeAlarm.id}/acknowledge`); } catch(e) {}
    setActiveAlarm(null);
  };

  const handleSnooze = async () => {
    if (!activeAlarm) return;
    stopSound();
    try { await api.post(`/calendar/${activeAlarm.id}/snooze`, { alarm_id: activeAlarm.alarm_id, snooze_minutes: snoozeMinutes }); } catch(e) {}
    setActiveAlarm(null);
  };

  const fmtDateTime = (d, t) => {
    if (!d) return '';
    const dt = d.split('T')[0];
    const date = new Date(dt + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return t ? `${date} at ${t.slice(0, 5)}` : date;
  };

  if (!activeAlarm) return null;

  return (
    <Modal isOpen={true} onClose={handleAcknowledge} size="sm" isCentered closeOnOverlayClick={false}>
      <ModalOverlay bg="blackAlpha.700" /><ModalContent borderWidth="3px" borderColor="red.400">
        <ModalHeader fontSize="md" color="red.600">
          <HStack><FiBell size={20} /><Text>Alarm</Text></HStack>
        </ModalHeader>
        <ModalBody>
          <VStack spacing={2} align="start">
            <Text fontWeight="700" fontSize="16px">{activeAlarm.title}</Text>
            <Text fontSize="12px" color="gray.600">{fmtDateTime(activeAlarm.start_date, activeAlarm.start_time)}</Text>
            {activeAlarm.project_name && <Badge colorScheme="blue" fontSize="10px">{activeAlarm.project_name}</Badge>}
            {activeAlarm.task_title && <Badge colorScheme="purple" fontSize="10px">{activeAlarm.task_title}</Badge>}
            <Box bg="orange.50" p={2} borderRadius="md" w="100%">
              <Text fontSize="11px" color="orange.700">
                {activeAlarm.minutes_before} minutes warning
              </Text>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2} w="100%">
            <Select size="sm" value={snoozeMinutes} onChange={e => setSnoozeMinutes(parseInt(e.target.value))} w="100px">
              <option value={5}>5 min</option>
              <option value={10}>10 min</option>
              <option value={15}>15 min</option>
              <option value={30}>30 min</option>
              <option value={60}>1 hour</option>
            </Select>
            <Button size="sm" colorScheme="orange" onClick={handleSnooze}>Snooze</Button>
            <Button size="sm" colorScheme="green" onClick={handleAcknowledge} flex={1}>
              <HStack><FiBellOff /><Text>Acknowledge</Text></HStack>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
